import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LangProvider } from "@/i18n/LangContext";
import { CookieBanner } from "@/components/site/CookieBanner";
import { GoogleTagManager } from "@/components/site/GoogleTagManager";
import { MicrosoftClarity } from "@/components/site/MicrosoftClarity";
import { SeoHeadLinks } from "@/components/site/SeoHeadLinks";
import { SiteSchema } from "@/components/site/SiteSchema";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import Lakarintyg from "./pages/Lakarintyg.tsx";
import Kontakt from "./pages/Kontakt.tsx";
import Boka from "./pages/Boka.tsx";
import Faq from "./pages/Faq.tsx";
import InforBesoket from "./pages/InforBesoket.tsx";
import SaFungerarDet from "./pages/SaFungerarDet.tsx";
import Guider from "./pages/Guider.tsx";
import Foretag from "./pages/Foretag.tsx";
import ForetagArbetsmiljo from "./pages/ForetagArbetsmiljo.tsx";
import ForetagDrogtestning from "./pages/ForetagDrogtestning.tsx";
import ForetagTjanster from "./pages/ForetagTjanster.tsx";
import ForetagKontakt from "./pages/ForetagKontakt.tsx";
import ForetagBokaTid from "./pages/ForetagBokaTid.tsx";
import Halsokontroller from "./pages/Halsokontroller.tsx";
import CertificateDetail from "./pages/CertificateDetail.tsx";
import OmOss from "./pages/OmOss.tsx";
import Bekraftelse from "./pages/Bekraftelse.tsx";
import StartaArende from "./pages/StartaArende.tsx";
import Drogtestning from "./pages/Drogtestning.tsx";
import DrogtestningPrivat from "./pages/DrogtestningPrivat.tsx";
import DrogtestningForIntyg from "./pages/DrogtestningForIntyg.tsx";
import DrogtestningBoka from "./pages/DrogtestningBoka.tsx";
import DrogtestningForfragan from "./pages/DrogtestningForfragan.tsx";
import Provtagning from "./pages/Provtagning.tsx";
import GuideDetail from "./pages/GuideDetail.tsx";
import LegalDocument from "./pages/LegalDocument.tsx";

const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const FindCertificateFab = lazy(() =>
  import("@/components/site/FindCertificateFab").then((m) => ({ default: m.FindCertificateFab })),
);

/**
 * App content that must sit inside a Router (BrowserRouter or StaticRouter)
 * and HelmetProvider.
 */
export const AppShell = () => {
  return (
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <ScrollToTop />
      <LangProvider>
        <GoogleTagManager />
        <MicrosoftClarity />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/en" element={<Index />} />
            <Route path="/lakarintyg" element={<Lakarintyg />} />
            <Route path="/läkarintyg" element={<Lakarintyg />} />
            <Route path="/en/lakarintyg" element={<Lakarintyg />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/en/contact" element={<Kontakt />} />
            <Route path="/boka-tid" element={<Boka />} />
            <Route path="/en/book-appointment" element={<Boka />} />
            <Route path="/fragor-och-svar" element={<Faq />} />
            <Route path="/en/faq" element={<Faq />} />
            <Route path="/infor-besoket" element={<InforBesoket />} />
            <Route path="/en/before-visit" element={<InforBesoket />} />
            <Route path="/sa-fungerar-det" element={<SaFungerarDet />} />
            <Route path="/en/how-it-works" element={<SaFungerarDet />} />
            <Route path="/guider" element={<Guider />} />
            <Route path="/en/guides" element={<Guider />} />
            <Route path="/foretag" element={<Foretag />} />
            <Route path="/foretag/arbetsmiljo" element={<ForetagArbetsmiljo />} />
            <Route path="/foretag/drogtestning" element={<ForetagDrogtestning />} />
            <Route path="/foretag/tjanster" element={<ForetagTjanster />} />
            <Route path="/foretag/kontakt" element={<ForetagKontakt />} />
            <Route path="/foretag/boka-tid" element={<ForetagBokaTid />} />
            <Route path="/en/companies" element={<Foretag />} />
            <Route path="/en/companies/occupational-health" element={<ForetagArbetsmiljo />} />
            <Route path="/en/companies/drug-testing" element={<ForetagDrogtestning />} />
            <Route path="/en/companies/services" element={<ForetagTjanster />} />
            <Route path="/en/companies/contact" element={<ForetagKontakt />} />
            <Route path="/en/companies/book" element={<ForetagBokaTid />} />
            <Route path="/halsokontroller" element={<Halsokontroller />} />
            <Route path="/en/health-checks" element={<Halsokontroller />} />
            <Route path="/om-oss" element={<OmOss />} />
            <Route path="/en/about-us" element={<OmOss />} />
            <Route path="/integritetspolicy" element={<LegalDocument docId="privacy" />} />
            <Route path="/en/privacy-policy" element={<LegalDocument docId="privacy" />} />
            <Route path="/cookie-policy" element={<LegalDocument docId="cookies" />} />
            <Route path="/cookiepolicy" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="/en/cookie-policy" element={<LegalDocument docId="cookies" />} />
            <Route path="/anvandarvillkor" element={<LegalDocument docId="terms" />} />
            <Route path="/en/terms-of-use" element={<LegalDocument docId="terms" />} />
            <Route path="/bekraftelse" element={<Bekraftelse />} />
            <Route path="/en/confirmation" element={<Bekraftelse />} />
            <Route path="/starta-arende" element={<StartaArende />} />
            <Route path="/en/start-case" element={<StartaArende />} />
            <Route path="/provtagning" element={<Provtagning />} />
            <Route path="/en/sample-collection" element={<Provtagning />} />
            <Route path="/drogtestning" element={<Drogtestning />} />
            <Route path="/drogtestning/privat" element={<DrogtestningPrivat />} />
            <Route path="/drogtestning/for-intyg" element={<DrogtestningForIntyg />} />
            <Route path="/drogtestning/salivtest" element={<Navigate to="/lakarintyg/salivtest" replace />} />
            <Route path="/drogtestning/urinprov" element={<Navigate to="/lakarintyg/urinprov" replace />} />
            <Route path="/drogtestning/boka/:slug" element={<DrogtestningBoka />} />
            <Route path="/drogtestning/forfragan" element={<DrogtestningForfragan />} />
            <Route path="/en/drug-testing" element={<Drogtestning />} />
            <Route path="/en/drug-testing/private" element={<DrogtestningPrivat />} />
            <Route path="/en/drug-testing/for-certificate" element={<DrogtestningForIntyg />} />
            <Route
              path="/en/drug-testing/saliva"
              element={<Navigate to="/en/medical-certificates/saliva" replace />}
            />
            <Route
              path="/en/drug-testing/urine"
              element={<Navigate to="/en/medical-certificates/urine" replace />}
            />
            <Route path="/en/drug-testing/book/:slug" element={<DrogtestningBoka />} />
            <Route path="/en/drug-testing/enquiry" element={<DrogtestningForfragan />} />
            <Route path="/lakarintyg/:slug" element={<CertificateDetail />} />
            <Route path="/en/medical-certificates/:slug" element={<CertificateDetail />} />
            <Route path="/guider/:category/:slug" element={<GuideDetail />} />
            <Route path="/en/guides/:category/:slug" element={<GuideDetail />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {/* After Routes so PageMeta has published title/description for this pass */}
        <SeoHeadLinks />
        <SiteSchema />
        <Suspense fallback={null}>
          <FindCertificateFab />
        </Suspense>
        <CookieBanner />
      </LangProvider>
    </TooltipProvider>
  );
};

export default AppShell;
